import { useBlockEditorMarkdown } from '@/components/tiptap/vMarkdown/useBlockEditor';
import { getSignupTemplateDescriptionPlaceholderKey } from '@/features/onboarding-v2/utils/getSignupTemplateDescriptionPlaceholderKey';
import { type ClinicianTypeInput } from '@/lib/types';
import { useTranslations } from '@heidi/runtime/i18n';
import { type CountryType } from '@heidi/utils/countries';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useMutationPublicTemplateSuggestions } from './useMutationPublicTemplateSuggestions';

interface UseTemplateCreationEditorProps {
  templateDescription: string;
  specialty: ClinicianTypeInput | null;
  country: CountryType | null;
  onTemplateDescriptionChange: (value: string) => void;
  isLoading?: boolean;
}

export function useTemplateCreationEditor({
  templateDescription,
  specialty,
  country,
  onTemplateDescriptionChange,
  isLoading = false,
}: UseTemplateCreationEditorProps) {
  const { t } = useTranslations();

  const templatePlaceholderKey = useMemo(
    () => getSignupTemplateDescriptionPlaceholderKey(specialty),
    [specialty]
  );

  const templatePlaceholder = useMemo(
    () => t(templatePlaceholderKey),
    [t, templatePlaceholderKey]
  );

  const { editor } = useBlockEditorMarkdown({
    content: templateDescription,
    editable: !isLoading,
    placeholder: templatePlaceholder,
    showPlaceholderWhenDisabled: true,
    autofocus: true,
    isTemplateEditor: true,
  });

  const [isFocused, setIsFocused] = useState(false);

  // Handle editor focus/blur events
  useEffect(() => {
    if (!editor) return;

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    editor.on('focus', handleFocus);
    editor.on('blur', handleBlur);

    return () => {
      editor.off('focus', handleFocus);
      editor.off('blur', handleBlur);
    };
  }, [editor]);

  // Suggestions fetching
  const {
    data: suggestionsData,
    isPending: isSuggestionsPending,
    mutateAsync: fetchSuggestions,
  } = useMutationPublicTemplateSuggestions();

  const suggestions = suggestionsData?.suggestions ?? [];

  const debouncedFetchSuggestions = useDebouncedCallback(async (text: string) => {
    // Only fetch if text is under 100 characters
    if (text.length <= 100) {
      await fetchSuggestions({
        text,
        specialtyName: specialty?.name,
        country: country?.countryCode,
      });
    }
  }, 300);

  // Initial suggestions fetch
  useEffect(() => {
    void fetchSuggestions({
      text: templateDescription,
      specialtyName: specialty?.name,
      country: country?.countryCode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only fetch on mount
  }, []);

  // Handle editor content updates
  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      const text = editor.getText().trim();
      if (text !== templateDescription) {
        onTemplateDescriptionChange(text);
        void debouncedFetchSuggestions(text);
      }
    };

    editor.on('update', handleUpdate);
    return () => {
      editor.off('update', handleUpdate);
    };
  }, [
    editor,
    templateDescription,
    onTemplateDescriptionChange,
    debouncedFetchSuggestions,
  ]);

  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      if (editor) {
        editor.commands.setContent(suggestion);
        onTemplateDescriptionChange(suggestion);
        void debouncedFetchSuggestions(suggestion);
      }
    },
    [editor, onTemplateDescriptionChange, debouncedFetchSuggestions]
  );

  const handleCardClick = useCallback(() => {
    if (editor && !isLoading) {
      editor.commands.focus();
    }
  }, [editor, isLoading]);

  return {
    editor,
    isFocused,
    suggestions,
    isSuggestionsPending,
    handleSuggestionClick,
    handleCardClick,
  };
}
